import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/system';
import { useQuery } from '@tanstack/react-query';
import { find, first, fromPairs, isBoolean, isEmpty, keyBy, map, startCase, toString } from 'lodash';
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { getModel } from 'api/platform';
import CopyableText from 'components/atoms/CopyableText';
import { UserIdentity } from 'components/atoms/UserIdentity';
import { ExternalSlideTransferJob, InferenceParams, Job, JobStatus, JobType } from 'interfaces/job';
import { formatDateTimeForUser } from 'utils/dateTime';
import { getModelId } from 'utils/models';
import { useUiSettings } from 'utils/queryHooks/uiConstantsHooks';
import { useStudies } from 'utils/queryHooks/useStudies';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { ExternalSlideTransferJobDetailsSection } from './ExternalSlideTransferJobDetailsSection';
import { classNamesOptions, modelTypeCell, modelTypeOptions } from './inferenceFieldsOptions';
import { IngestJobDetailsSection } from './IngestJobDetailsSection';
import { JobArchiveControl } from './JobArchiveControl';
import { JobExperimentResultsSection } from './JobExperimentResultsSection';
import { getJobOutcomeSummary } from './jobOutcomeSummary';
import { JobParamsJsonTree } from './JobParamsJsonTree';
import { getJobStatusDisplayLabel, JobStatusChip } from './JobStatusChip';
import { PrepareCustomerResultsJobParams } from './PrepareCustomerResultsJobParams';
import { SubtaskDetails } from './SubtaskDetails';
import { TileGenerationJobDetailsSection } from './TileGenerationJobDetailsSection';
import { stringToDisplay } from './utils';

const SKIP = 'skip';

type JobWithBackendMetadata = Partial<Job> & {
  params?: Record<string, unknown>;
  results?: Record<string, unknown>;
};

const getJobAwsRefs = (job?: Partial<Job>) => {
  const { params, results } = (job || {}) as JobWithBackendMetadata;
  const rawAwsRefs = params?.aws_refs || params?.awsRefs || results?.aws_refs || results?.awsRefs;

  if (!rawAwsRefs || typeof rawAwsRefs !== 'object' || Array.isArray(rawAwsRefs)) {
    return [];
  }

  return Object.entries(rawAwsRefs as Record<string, unknown>).filter(([, value]) => !isEmpty(toString(value)));
};

interface JobDataProps {
  job?: Partial<Job>;
  isLoading?: boolean;
  hasPrefetchedData?: boolean;
  onClosed?: React.MouseEventHandler<HTMLButtonElement>;
}

export const JobDetails: React.FC<JobDataProps> = ({ job, hasPrefetchedData, isLoading = false, onClosed }) => {
  const theme = useTheme();
  const location = useLocation();
  const showDrawerCloseButton = Boolean(onClosed);
  const { labId } = useCurrentLabId();
  const parentJobSearchParams = new URLSearchParams(location.search);

  if (job?.parentId) {
    parentJobSearchParams.set('jobDrawerOpen', '1');
    parentJobSearchParams.set('currentJobId', job.parentId);
  }

  const { data: studies } = useStudies(labId);
  const modelPath = job?.type === JobType.Inference ? first((job?.params as InferenceParams)?.modelPaths) : undefined;
  const modelId = getModelId(modelPath || '');
  const { data: modelData } = useQuery({
    queryKey: ['model', modelId],
    queryFn: ({ signal }) => getModel(modelId || '', signal),
    enabled: job?.type === JobType.Inference && Boolean(modelId),
  });

  const { uiSettings } = useUiSettings();
  const currStudy = job?.studyId ? find(studies, (study) => job.studyId === study.id)?.name : undefined;

  const requestSenderToDisplayText = fromPairs(
    map(uiSettings?.enumDisplayNames?.['requestSender'], ({ value, label }) => [value, label]) || []
  );
  const jobStatusToDisplayText = fromPairs(
    map(uiSettings?.enumDisplayNames?.['jobStatus'], ({ value, label }) => [value, label]) || []
  );
  jobStatusToDisplayText[SKIP] = 'Skipped';

  const modelTypeOptionsByValue = keyBy(modelTypeOptions, 'value');
  const classNamesOptionsByValue = keyBy(classNamesOptions, 'value');

  const assignmentIds =
    job?.type === JobType.Inference ? stringToDisplay((job?.params as InferenceParams)?.assignmentIds) : '';
  const classNames =
    job?.type === JobType.Inference
      ? stringToDisplay(
          map(
            (job?.params as InferenceParams)?.classNames,
            (className) => classNamesOptionsByValue[className]?.text || className
          )
        )
      : '';
  // heatmapNames and modelPaths  saved as array but currently there is only one heatmap and model per job
  const modelPaths =
    job?.type === JobType.Inference ? stringToDisplay((job?.params as InferenceParams)?.modelPaths) : '';
  const heatmapNames =
    job?.type === JobType.Inference ? stringToDisplay((job?.params as InferenceParams)?.heatmapNames) : '';

  const runType = job?.type === JobType.Inference ? (job?.params as InferenceParams)?.runType : undefined;

  const tissueMaskFromAnnotations =
    job?.type === JobType.Inference ? (job?.params as InferenceParams)?.tissueMaskFromAnnotations : undefined;

  const tissueSegmentationModelOverride =
    job?.type === JobType.Inference ? (job?.params as InferenceParams)?.tissueSegmentationModelOverride : undefined;
  const awsRefs = getJobAwsRefs(job);
  const hasExternalDetails = Boolean(job?.externalTaskId || job?.externalTaskLink || !isEmpty(awsRefs));
  const ingestJob = job?.type === JobType.Ingest ? job : undefined;
  const ingestJobId = ingestJob?.id;
  const shouldShowIngestDetails =
    Boolean(ingestJobId) && ingestJob?.status !== JobStatus.Pending && ingestJob?.status !== JobStatus.Running;
  const jobOutcomeSummary = getJobOutcomeSummary(job || {});

  return (
    <Grid container p={2} direction="column" spacing={2} flexWrap="nowrap">
      <Grid container item alignItems="flex-start">
        <Grid item xs={showDrawerCloseButton ? 10 : 12}>
          {job?.parentId && (
            <Button
              component={RouterLink}
              to={{ pathname: location.pathname, search: parentJobSearchParams.toString() }}
              color="inherit"
              size="small"
              startIcon={<ArrowBackIcon fontSize="small" />}
              sx={{ mb: 1, pl: 0 }}
            >
              Back to parent job
            </Button>
          )}
          <CopyableText variant="h3" sx={{ wordBreak: 'break-word' }}>
            {job?.name || job?.orchestrationId}
          </CopyableText>
          {Boolean(job?.name) && job?.id && (
            <CopyableText
              variant="h5"
              color={theme.palette.text.secondary}
              sx={{ wordBreak: 'break-word' }}
              textForCopy={job?.id}
            >
              {job?.id}
            </CopyableText>
          )}
          {Boolean(job?.name && job?.orchestrationId) && (
            <CopyableText
              variant="h5"
              color={theme.palette.text.secondary}
              sx={{ wordBreak: 'break-word' }}
              textForCopy={job?.orchestrationId}
            >
              (Orchestration ID: {job?.orchestrationId})
            </CopyableText>
          )}
          {job?.id && (
            <Box sx={{ mt: 1 }}>
              <JobArchiveControl
                job={{
                  id: job.id,
                  name: job.name,
                  status: job.status,
                  userId: job.userId,
                  deletedAt: job.deletedAt,
                }}
                labeled
              />
            </Box>
          )}
        </Grid>
        {showDrawerCloseButton && (
          <Grid item xs={2} textAlign="right" justifyContent="flex-end" container>
            <IconButton onClick={onClosed} sx={{ p: 0 }}>
              <CloseIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ pt: 2 }} />

      <Grid item>
        <Typography variant="body1">{job?.description}</Typography>
      </Grid>

      <Grid item container direction="column">
        <Grid item container spacing={1}>
          <Grid item>
            <Typography variant="body1">Started at: </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1">{formatDateTimeForUser(job?.startedAt)}</Typography>
          </Grid>
        </Grid>
        <Grid item container spacing={1}>
          <Grid item>
            <Typography variant="body1">Source: </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1"> {requestSenderToDisplayText[job?.requestSender || '']}</Typography>
          </Grid>
        </Grid>
        <Grid item container spacing={1}>
          <Grid item>
            <Typography variant="body1">Run by: </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body1">
              <UserIdentity userId={job?.userId} displayName={job?.submittedBy} usersById={job?.usersById} />
            </Typography>
          </Grid>
        </Grid>
        {job?.deletedAt && (
          <>
            <Grid item container spacing={1}>
              <Grid item>
                <Typography variant="body1">Archived at: </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body1">{formatDateTimeForUser(job.deletedAt)}</Typography>
              </Grid>
            </Grid>
            <Grid item container spacing={1}>
              <Grid item>
                <Typography variant="body1">Archived by: </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body1">
                  <UserIdentity userId={job.deletedBy} usersById={job.usersById} />
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
      </Grid>

      <Grid item container spacing={1}>
        <Grid item>
          <Typography variant="body1">Status: </Typography>
        </Grid>
        <Grid item>
          {jobOutcomeSummary.displayStatus ? (
            <Grid container direction="column" spacing={0.5}>
              <Grid item>
                <JobStatusChip
                  status={jobOutcomeSummary.displayStatus}
                  label={
                    jobOutcomeSummary.displayLabel ||
                    getJobStatusDisplayLabel(jobOutcomeSummary.displayStatus, jobStatusToDisplayText)
                  }
                />
              </Grid>
              {jobOutcomeSummary.details ? (
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    {jobOutcomeSummary.details}
                  </Typography>
                </Grid>
              ) : null}
            </Grid>
          ) : null}
        </Grid>
      </Grid>
      <Grid item container spacing={1}>
        <Grid item>
          <Typography variant="body1">Status Message: </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1">{job?.statusMessage ?? ''}</Typography>
        </Grid>
      </Grid>
      {job?.id ? (
        <Grid item>
          <JobExperimentResultsSection jobId={job.id} />
        </Grid>
      ) : null}
      {shouldShowIngestDetails && ingestJobId && ingestJob ? (
        <IngestJobDetailsSection jobId={ingestJobId} job={ingestJob} />
      ) : null}
      {job?.type === JobType.TileGeneration && <TileGenerationJobDetailsSection job={job} />}
      {job?.type === JobType.ExternalSlideTransfer && (
        <ExternalSlideTransferJobDetailsSection job={job as ExternalSlideTransferJob} />
      )}
      {(!hasPrefetchedData && isLoading) || hasExternalDetails ? (
        <Grid item>
          {!hasPrefetchedData && isLoading ? (
            <Skeleton variant="rectangular" height={150} />
          ) : (
            <Accordion defaultExpanded slotProps={{ transition: { unmountOnExit: true } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                <Typography variant="h4">External Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container direction="column" spacing={1}>
                  {job?.externalTaskId && (
                    <Grid item container spacing={1}>
                      <Grid item>
                        <Typography variant="body1">Task ID:</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <CopyableText variant="body1" sx={{ wordBreak: 'break-word' }}>
                          {job.externalTaskId}
                        </CopyableText>
                      </Grid>
                    </Grid>
                  )}
                  {job?.externalTaskLink && (
                    <Grid item container spacing={1}>
                      <Grid item>
                        <Typography variant="body1">Link:</Typography>
                      </Grid>
                      <Grid xs={10} item sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Tooltip placement="top-start" title={job.externalTaskLink}>
                          <MuiLink color="inherit" underline="hover" href={job.externalTaskLink} target="_blank">
                            {job.externalTaskLink}
                          </MuiLink>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  )}
                  {!isEmpty(awsRefs) && (
                    <Grid item container direction="column" spacing={0.5}>
                      <Grid item>
                        <Typography variant="body1">AWS References:</Typography>
                      </Grid>
                      {map(awsRefs, ([key, value]) => (
                        <Grid item container spacing={1} key={key}>
                          <Grid item>
                            <Typography variant="body2" color="text.secondary">
                              {startCase(key)}:
                            </Typography>
                          </Grid>
                          <Grid item xs={9}>
                            <CopyableText variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {toString(value)}
                            </CopyableText>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </Grid>
      ) : null}
      <Grid item>
        {!hasPrefetchedData && isLoading ? (
          <Skeleton variant="rectangular" height={150} />
        ) : (
          <Accordion defaultExpanded slotProps={{ transition: { unmountOnExit: true } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
              <Typography variant="h4">Parameters</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {heatmapNames && (
                  <Grid item container spacing={1}>
                    <Grid item>
                      <Typography variant="body1">Heatmap name: </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1"> {heatmapNames}</Typography>
                    </Grid>
                  </Grid>
                )}
                <Grid item container spacing={1}>
                  <Grid item>
                    <Typography variant="body1">Study: </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body1"> {currStudy}</Typography>
                  </Grid>
                </Grid>
                {runType && (
                  <Grid item container spacing={1}>
                    <Grid item>
                      <Typography variant="body1">Model type: </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1">{modelTypeOptionsByValue[runType]?.text || runType}</Typography>
                    </Grid>
                  </Grid>
                )}
                {job?.type === JobType.Inference && (
                  <Grid item container spacing={1}>
                    <Grid item>
                      <Typography variant="body1">Model: </Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Tooltip placement="top-start" title={modelPaths}>
                        <Typography variant="body1" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {modelPaths}
                        </Typography>
                      </Tooltip>
                    </Grid>
                  </Grid>
                )}
                {modelData?.meta?.modelType === modelTypeCell.apiModelValue && (
                  <>
                    <Grid item container spacing={1}>
                      <Grid item>
                        <Typography variant="body1">dedup Value: </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">{(job?.params as InferenceParams)?.dedupValue}</Typography>
                      </Grid>
                    </Grid>
                    <Grid item container spacing={1}>
                      <Grid item>
                        <Typography variant="body1">Use Dynamic Cell Detection: </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">
                          {isBoolean((job?.params as InferenceParams)?.useDynamicCellDetection) &&
                            startCase(toString((job?.params as InferenceParams)?.useDynamicCellDetection))}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid item container spacing={1}>
                      <Grid item>
                        <Typography variant="body1">Dynamic Cell Detection Config: </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">
                          {(job?.params as InferenceParams)?.dynamicCellDetectionConfig}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                )}
                {job?.type === JobType.Inference && (
                  <Grid item container spacing={1}>
                    <Grid item>
                      <Typography variant="body1">ROI mask: </Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant="body1">
                        {isBoolean(tissueMaskFromAnnotations) && startCase(toString(tissueMaskFromAnnotations))}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
                {!isEmpty(tissueSegmentationModelOverride) && (
                  <Grid item container spacing={1}>
                    <Grid item>
                      <Typography variant="body1">TSM Model: </Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Tooltip placement="top-start" title={tissueSegmentationModelOverride}>
                        <Typography variant="body1" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tissueSegmentationModelOverride}
                        </Typography>
                      </Tooltip>
                    </Grid>
                  </Grid>
                )}
                {tissueMaskFromAnnotations && (
                  <Grid>
                    <Grid item container spacing={1}>
                      <Grid item>
                        <Typography variant="body1">Assignments Ids: </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">{assignmentIds}</Typography>
                      </Grid>
                    </Grid>
                    <Grid item container spacing={1}>
                      <Grid item>
                        <Typography variant="body1">Class names: </Typography>
                      </Grid>
                      <Grid item>
                        <Typography variant="body1">{classNames}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                {job?.type === JobType.PrepareCustomerResults && <PrepareCustomerResultsJobParams job={job} />}
              </Box>
              {job?.params && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body1">Raw Parameters Data</Typography>
                    <CopyableText
                      textForCopy={JSON.stringify(job?.params)}
                      sx={{ ml: 1 }}
                      tooltipText="Copy Raw Parameters Data"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </CopyableText>
                  </AccordionSummary>
                  <AccordionDetails>
                    <JobParamsJsonTree data={job?.params} />
                  </AccordionDetails>
                </Accordion>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </Grid>
      {job?.type !== JobType.PrepareCustomerResults && <SubtaskDetails job={job || {}} isLoading={isLoading} />}
    </Grid>
  );
};
